import { useState, useMemo } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { FloatItem, Twinkle } from "./FloatingDecor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const CONFETTI_HEART =
  "M12 21c-4.6-3.7-9-7-9-11.2C3 6.6 5.4 4.5 8 4.5c1.6 0 3.1.8 4 2.1.9-1.3 2.4-2.1 4-2.1 2.6 0 5 2.1 5 5.3 0 4.2-4.4 7.5-9 11.2z";
const CONFETTI_STAR = "M12 2l2.2 7.8L22 12l-7.8 2.2L12 22l-2.2-7.8L2 12l7.8-2.2L12 2z";
const CONFETTI_COLORS = ["#D88C9A", "#E0A3AC", "#C57B89", "#D4AF37"];

const HeartConfetti = () => {
  const pieces = useMemo(
    () =>
      Array.from({ length: 40 }, (_, i) => ({
        id: i,
        isHeart: i % 3 !== 0,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        size: 10 + Math.random() * 14,
        xBurst: (Math.random() - 0.5) * 520,
        yUp: -(90 + Math.random() * 280),
        xEnd: (Math.random() - 0.5) * 720,
        yDown: 280 + Math.random() * 420,
        rot: (Math.random() - 0.5) * 720,
        delay: Math.random() * 0.25,
        duration: 2.1 + Math.random() * 1.2,
      })),
    []
  );
  return (
    <div
      className="absolute inset-0 overflow-hidden pointer-events-none z-20"
      aria-hidden="true"
      data-testid="signup-confetti"
    >
      {pieces.map((pc) => (
        <motion.svg
          key={pc.id}
          viewBox="0 0 24 24"
          fill={pc.color}
          className="absolute left-1/2 top-1/2"
          style={{ width: pc.size, height: pc.size }}
          initial={{ x: 0, y: 0, opacity: 0, scale: 0, rotate: 0 }}
          animate={{
            x: [0, pc.xBurst, pc.xEnd],
            y: [0, pc.yUp, pc.yDown],
            opacity: [0, 1, 0],
            scale: [0, 1.1, 0.7],
            rotate: [0, pc.rot * 0.5, pc.rot],
          }}
          transition={{ duration: pc.duration, delay: pc.delay, ease: "easeOut", times: [0, 0.35, 1] }}
        >
          <path d={pc.isHeart ? CONFETTI_HEART : CONFETTI_STAR} />
        </motion.svg>
      ))}
    </div>
  );
};

export default function NotifyForm() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("loading");
    try {
      const { data } = await axios.post(`${API}/notify`, { email: email.trim() });
      setMessage(data.message);
      setState("success");
    } catch {
      setMessage("Hmm, that email doesn't look quite right — try again?");
      setState("error");
    }
  };

  return (
    <section
      id="notify"
      data-testid="notify-section"
      className="relative overflow-hidden bg-[#F9F6F0] border-t border-[#E8E1D9]"
    >
      <div
        aria-hidden="true"
        className="absolute -top-24 right-[10%] w-72 h-72 rounded-full bg-[#E6C2C6]/40 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-28 left-[6%] w-72 h-72 rounded-full bg-[#D4AF37]/15 blur-3xl"
      />
      <FloatItem type="heart" filled color="#D88C9A" className="w-5 h-5 top-14 left-[10%]" delay={0.5} duration={8} drift={14} />
      <FloatItem type="gift" className="w-9 h-9 bottom-14 right-[9%] text-[#C57B89]" delay={0.9} duration={10} drift={16} sway={9} />
      <FloatItem type="bow" className="w-8 h-8 top-20 right-[14%] text-[#E0A3AC]" delay={1.2} duration={11} drift={10} sway={6} />
      <Twinkle className="w-4 h-4 top-28 left-[24%]" delay={1.6} />

      {state === "success" && <HeartConfetti />}
      <div className="relative max-w-3xl mx-auto px-6 py-28 md:py-36 text-center">
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE }}
          className="text-[0.65rem] tracking-[0.5em] uppercase text-[#8A7D78]"
        >
          Coming Soon
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
          className="font-serif text-4xl md:text-6xl font-light tracking-tight mt-5 text-[#2C2421]"
        >
          Be the first to <em className="text-givi-gradient font-medium">unwrap</em> GIVI.
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
          className="mt-5 text-base md:text-lg font-light text-[#2C2421]/70"
        >
          Something beautiful is on its way to givi.oneeight.au
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}
          className="mt-12"
        >
          <p
            className="font-serif italic text-2xl md:text-3xl text-[#2C2421]"
            data-testid="launch-message"
          >
            Our doors open soon — see you at the{" "}
            <span className="text-givi-gradient font-medium">reveal</span>.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
